import Immutable from 'seamless-immutable';

import { BRAND_FACET, SINGLE_SELECT_FACET_FIELDS } from 'components/search/FacetAccordion/FacetAccordion.constants';
import type { FacetValueVariantType } from 'components/search/FacetAccordion/FacetAccordion.types';
import type { FacetDataType, FacetValueType } from 'types/search';

export function getFacetVariant(facet: FacetDataType): FacetValueVariantType {
  if (isSingleSelectFacet({ facetField: facet.facetField })) {
    return 'radio';
  }

  if (facet.symbolicSizingField) {
    return 'button';
  }

  return 'checkbox';
}

export function isSingleSelectFacet({ facetField }: { facetField: string }): boolean {
  return !!SINGLE_SELECT_FACET_FIELDS[facetField as keyof typeof SINGLE_SELECT_FACET_FIELDS];
}

export function getSelectedFacetNames({ facet }: { facet: FacetDataType }): string | null {
  const selectedValues = facet.values.filter(value => value.selected).map(value => value.displayName);

  if (selectedValues.length === 0) {
    return null;
  }

  const mutableSelections = Immutable.asMutable(selectedValues);

  // Sort numerically if all values are numbers, otherwise sort alphabetically
  const sortedSelections = mutableSelections.sort((a, b) => {
    const numA = parseFloat(a);
    const numB = parseFloat(b);

    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }

    return a.localeCompare(b);
  });

  return sortedSelections.join(', ');
}

export function getSelectedFacetCount({ facet }: { facet: FacetDataType }): number {
  return facet.values.filter(value => value.selected).length;
}

export function groupFacetValues({ values, facetField }: { values: FacetValueType[]; facetField: string }): Record<string, FacetValueType[]> {
  if (facetField === BRAND_FACET) {
    return values.reduce(
      (acc, value) => {
        const firstChar = value.displayName.charAt(0);
        const groupKey = /\d/.test(firstChar) ? '#' : firstChar.toUpperCase();
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey]!.push(value);
        return acc;
      },
      {} as Record<string, FacetValueType[]>
    );
  }

  return { '': values };
}

export function sortGroupKeys({ keys }: { keys: string[] }): string[] {
  return keys.sort((a, b) => {
    if (a === '#') return -1;
    if (b === '#') return 1;
    return a.localeCompare(b);
  });
}
